import { useCallback, useMemo, useState } from "react";
import { buildStoryShowcase } from "../../hooks/home-story-showcase";
import { useHomeBoot } from "../../hooks/useHomeBoot";
import type { HomeStoryStatus } from "../../hooks/useHomeStoryScene";
import { HomeCommunity } from "./components/HomeCommunity";
import { HomeFeatured } from "./components/HomeFeatured";
import { SiteFooter } from "../../components/site-footer/SiteFooter";
import { SiteHeader } from "../../components/site-header/SiteHeader";
import { HomeLoader } from "./components/HomeLoader";
import { HomeMaterials } from "./components/HomeMaterials";
import { HomeMaterialCharacters } from "./components/HomeMaterialCharacters";
import { HomeStory } from "./components/HomeStory";
import { useClientSession } from "../../hooks/useClientSession";
import { useHomeFeed } from "./useHomeFeed";
import "./home-shell.css";
import "./home-story.css";
import "./home-story-type.css";
import "./home-characters.css";
import "./home-character-scene.css";
import "./home-skip.css";
import "./home-featured.css";
import "./home-materials.css";
import "./home-community.css";
import "./home-motion.css";
import "./home-loader.css";

export default function HomePage() {
  const clientSession = useClientSession();
  const feed = useHomeFeed();
  const signedIn = Boolean(clientSession.session.id);
  const [sceneStatus, setSceneStatus] = useState<HomeStoryStatus>("loading");
  const onSceneStatus = useCallback((status: HomeStoryStatus) => setSceneStatus(status), []);
  const showcase = useMemo(
    () => (feed.materialsReady ? buildStoryShowcase(feed.materials, [...feed.featured, ...feed.latest]) : null),
    [feed.materialsReady, feed.materials, feed.featured, feed.latest],
  );
  const boot = useHomeBoot({ sceneSettled: sceneStatus !== "loading", dataSettled: feed.materialsReady });

  return (
    <div className={`home-page${boot.finished ? " is-booted" : ""}`}>
      {boot.active ? <HomeLoader progress={boot.progress} finished={boot.finished} /> : null}
      <SiteHeader session={clientSession.session} loading={clientSession.loading} onLogout={clientSession.logout} />
      <main>
        <HomeStory signedIn={signedIn} loadingSession={clientSession.loading} showcase={showcase} onStatusChange={onSceneStatus} />
        <HomeMaterialCharacters notes={feed.notePool} loading={feed.loading} />
        <HomeFeatured notes={feed.featured} loading={feed.loading} error={feed.error} onRetry={feed.reload} />
        <HomeMaterials materials={feed.materials} types={feed.materialTypes} loading={feed.loading} error={feed.error} onRetry={feed.reload} />
        <HomeCommunity notes={feed.latest} topics={feed.topics} loading={feed.loading} />
      </main>
      <SiteFooter />
    </div>
  );
}
